import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import './RadarChart.css';

interface RadarChartProps {
  labels?: string[];
  values?: number[];
  showLegend?: boolean;
}

const defaultLabels = ['Communication', 'Leadership', 'Problem Solving', 'Technical', 'Creativity', 'Adaptability'];
const defaultValues = [85, 78, 92, 88, 73, 80];

// Generate letter labels (a, b, c, d, e, f...)
const getLetterLabel = (index: number): string => {
  return String.fromCharCode(97 + index); // 97 is 'a' in ASCII
};

const RadarChart: React.FC<RadarChartProps> = ({
  labels = defaultLabels,
  values = defaultValues,
  showLegend = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  // Create letter labels for the chart
  const chartLabels = labels.map((_, index) => getLetterLabel(index));

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    Chart.register(...registerables);

    chartRef.current = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: chartLabels,
        datasets: [
          {
            label: 'Skill Proficiency',
            data: values,
            fill: false, // No fill/glow inside
            borderColor: '#a3e635',
            borderWidth: 2.5,
            pointRadius: 0, // No dots on edges
            pointHoverRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        events: [], // Disable all events - no hover/click
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }, // Disable tooltips
        },
        elements: {
          line: {
            tension: 0, // Sharp corners, no rounding
          },
        },
        scales: {
          r: {
            grid: { color: 'rgba(255,255,255,0.05)' },
            angleLines: { color: 'rgba(255,255,255,0.05)' },
            pointLabels: { 
              display: true,
              color: '#98a0a8', 
              font: { size: 12, weight: '500' },
            },
            ticks: {
              display: false, // Hide tick values
              stepSize: 20,
            },
            suggestedMin: 0,
            suggestedMax: 100,
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [labels, values]);

  return (
    <div className="radar-chart-container">
      <div className="radar-chart__canvas-wrapper">
        <canvas ref={canvasRef} />
      </div>
      {showLegend && (
        <div className="radar-chart__legend">
          {labels.map((label, index) => (
            <div key={label} className="radar-chart__legend-item">
              <span className="radar-chart__legend-label">
                <span className="radar-chart__legend-key">{getLetterLabel(index)}</span>
                {label}
              </span>
              <span className="radar-chart__legend-value">{values[index]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RadarChart;
